import postHogService from 'services/analytics/postHogService';
import { Action } from '../store';

// Define which actions you want to track
const TRACKABLE_ACTIONS = new Set([
  'SET_PROJECT_ID',
  'SET_ERROR',
  'SET_ACTIVE_TOPIC',
]);

// Map action types to analytics event names
const ACTION_TO_EVENT_MAP: Record<string, string> = {
  'SET_PROJECT_ID': 'project_loaded',
  'SET_ERROR': 'error_occurred',
  'SET_ACTIVE_TOPIC': 'topic_changed',
};

interface AnalyticsMiddlewareConfig {
  enabled?: boolean;
  debug?: boolean;
  customEventMapper?: (action: Action, state: any) => { eventName: string; properties: Record<string, any> } | null;
}

export const createAnalyticsMiddleware = (config: AnalyticsMiddlewareConfig = {}) => {
  const { enabled = true, debug = false, customEventMapper } = config;

  return (store: any) => (next: any) => (action: Action) => {
    // Call next first to get the updated state
    const result = next(action);
    
    if (!enabled || !TRACKABLE_ACTIONS.has(action.type)) {
      return result;
    }

    try {
      const state = store.getState();
      let eventName = ACTION_TO_EVENT_MAP[action.type];
      let properties: Record<string, any> = {
        action_type: action.type,
        timestamp: new Date().toISOString(),
      };

      // Use custom event mapper if provided
      if (customEventMapper) {
        const customEvent = customEventMapper(action, state);
        if (customEvent) {
          eventName = customEvent.eventName;
          properties = { ...properties, ...customEvent.properties };
        }
      }

      // Extract relevant data based on action type
      switch (action.type) {
        case 'SET_PROJECT_ID':
          properties = {
            ...properties,
            project_id: action.data
          };
          break;
          
        case 'SET_ERROR':
          properties = {
            ...properties,
            error_message: action.data?.err || action.data,
            project_id: state.projectId,
            current_topic: state.currentTopic,
          };
          break;
          
        case 'SET_ACTIVE_TOPIC':
          properties = {
            ...properties,
            topic_id: action.data?.id,
            project_id: state.projectId,
          };
          break;
          
        default:
          properties = {
            ...properties,
            action_data: action.data,
            project_id: state.projectId,
          };
      }

      if (debug) {
        console.log('Analytics Middleware:', { eventName, properties, action });
      }

      // Track the event
      if (eventName) {
        postHogService.track(eventName, properties);
      }
    } catch (error) {
      console.error('Analytics middleware error:', error);
    }

    return result;
  };
};

export default createAnalyticsMiddleware;
